import { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import "@/App.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TOKEN_KEY = "notenest_token";

// Always attach the Bearer token (if present) to every request.
// Defensive: localStorage can throw in private/locked-down browsers — never let
// that take down the request pipeline.
axios.interceptors.request.use((config) => {
  try {
    const t = localStorage.getItem(TOKEN_KEY);
    if (t) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${t}`;
    }
  } catch (_) {
    /* localStorage unavailable; send unauthenticated */
  }
  return config;
});

function describeAxiosError(e) {
  if (!e) return "Something went wrong. Please try again.";
  // Server replied with a real HTTP error
  if (e.response) {
    const detail = e.response.data && e.response.data.detail;
    if (detail) return formatApiError(detail);
    if (e.response.status === 401) return "That email or password didn't match.";
    if (e.response.status >= 500) return "Server error. Please try again in a moment.";
    return `Request failed (${e.response.status}).`;
  }
  // Request never got a response — network/CORS/offline
  if (e.code === "ECONNABORTED" || /timeout/i.test(e.message || "")) {
    return "The request timed out. Check your connection and try again.";
  }
  return "Couldn't reach the server. If this keeps happening, hard-refresh the page (Ctrl+Shift+R) or try again in a moment.";
}

function _tr(s) {
  // Translate a string via the global nnI18n helper if available.
  if (typeof window !== "undefined" && window.nnI18n && typeof window.nnI18n.t === "function") {
    return window.nnI18n.t(s);
  }
  return s;
}

function translateApiMsg(msg) {
  if (!msg || typeof msg !== "string") return msg;
  // Common backend / Pydantic English messages → translate the most user-facing parts
  // so users see a localized error instead of "value is not a valid email address...".
  if (/value is not a valid email address/i.test(msg)) {
    return _tr("Please enter a valid email address.");
  }
  if (/email already registered/i.test(msg)) {
    return _tr("Email already registered");
  }
  if (/invalid email or password/i.test(msg)) {
    return _tr("Invalid email or password");
  }
  return _tr(msg);
}

function formatApiError(detail) {
  if (detail == null) return _tr("Something went wrong. Please try again.");
  if (typeof detail === "string") return translateApiMsg(detail);
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? translateApiMsg(e.msg) : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return translateApiMsg(detail.msg);
  return String(detail);
}

function _passthroughQuery() {
  // When the React auth shell hands the user off to /notenest.html we need
  // to preserve a couple of query params that the inner app reacts to —
  // most importantly ?course_invite=<token> (the invite-accept landing
  // flow). Without this the React redirect would drop the token and the
  // user would land on an empty notes page.
  try {
    const sp = new URLSearchParams(window.location.search);
    const keep = new URLSearchParams();
    ["course_invite", "share", "session_id"].forEach((k) => {
      const v = sp.get(k);
      if (v) keep.set(k, v);
    });
    const qs = keep.toString();
    return qs ? `?${qs}` : "";
  } catch (_) {
    return "";
  }
}
function _notenestUrl() { return `/notenest.html${_passthroughQuery()}`; }

function saveSession(data) {
  if (data && data.access_token) {
    try { localStorage.setItem(TOKEN_KEY, data.access_token); } catch (_) {}
  }
}

function AuthThemeToggle() {
  const [mode, setMode] = useState(() => {
    try { return localStorage.getItem("nn_theme") || "light"; } catch { return "light"; }
  });
  function applyTheme(next) {
    const prefersDark = typeof window !== "undefined" && window.matchMedia &&
      window.matchMedia("(prefers-color-scheme: dark)").matches;
    const effective = next === "dark" || (next === "auto" && prefersDark) ? "dark" : "light";
    if (effective === "dark") document.documentElement.setAttribute("data-theme", "dark");
    else document.documentElement.removeAttribute("data-theme");
    try {
      const tag = document.querySelector('meta[name="theme-color"]');
      if (tag) tag.setAttribute("content", effective === "dark" ? "#161716" : "#0E7A3F");
    } catch {}
  }
  function toggle() {
    const next = mode === "dark" ? "light" : "dark";
    setMode(next);
    try { localStorage.setItem("nn_theme", next); } catch {}
    applyTheme(next);
  }
  const isDark = mode === "dark";
  return (
    <button
      type="button"
      onClick={toggle}
      className="auth-theme-toggle"
      data-testid="auth-theme-toggle"
      aria-label={isDark ? _tr("Switch to light mode") : _tr("Switch to dark mode")}
      title={isDark ? _tr("Switch to light mode") : _tr("Switch to dark mode")}
    >
      {isDark ? (
        <>
          <svg viewBox="0 0 14 14" fill="none" aria-hidden="true"><circle cx="7" cy="7" r="2.6" stroke="currentColor" strokeWidth="1.4"/><path d="M7 1.5v1.6M7 10.9v1.6M1.5 7h1.6M10.9 7h1.6M3.05 3.05l1.13 1.13M9.82 9.82l1.13 1.13M3.05 10.95l1.13-1.13M9.82 4.18l1.13-1.13" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/></svg>
          <span>{_tr("Light")}</span>
        </>
      ) : (
        <>
          <svg viewBox="0 0 14 14" fill="none" aria-hidden="true"><path d="M11 9.2a4.5 4.5 0 01-6.2-6.2A5 5 0 1011 9.2z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round"/></svg>
          <span>{_tr("Dark")}</span>
        </>
      )}
    </button>
  );
}

function Shell({ mode, children }) {
  // mode: 'login' | 'signup' — drives which tab is active
  const isLogin = mode === "login";
  // Carry the current search string across login↔signup so an invite token
  // (?course_invite=...) survives if the user toggles tabs before submitting.
  const qs = typeof window !== "undefined" ? window.location.search : "";
  return (
    <div className="auth-wrap" data-testid="auth-shell">
      <AuthThemeToggle />

      {/* Top brand row — small and centered above the card */}
      <div className="auth-brand">
        <span className="auth-logo" aria-hidden="true">
          <img src="/logo.png" alt="" />
        </span>
        <span className="auth-brand-text">NoteNest</span>
        <span className="auth-brand-dot" aria-hidden="true" />
        <span className="auth-brand-tag">{_tr("Study OS")}</span>
      </div>

      {/* Floating sample-note polaroid — pinned to the upper-left as
          decoration. Hidden on small viewports. Topic refreshed from
          Biology → a Business-school case study. */}
      <aside className="auth-sample" aria-hidden="true">
        <div className="auth-sample-eyebrow">{_tr("A note from management")}</div>
        <h3>{_tr("Porter's Five Forces")}</h3>
        <div className="auth-sample-rule" />
        <div className="auth-sample-sub">{_tr("Why this framework matters")}</div>
        <p>{_tr("Maps the competitive intensity of an industry to spot where profit pools really sit.")}</p>
        <ul>
          <li>{_tr("Threat of new entrants")}</li>
          <li>{_tr("Bargaining power of buyers")}</li>
          <li>{_tr("Rivalry among existing firms")}</li>
        </ul>
      </aside>

      {/* Cream landscape-pill card — the form lives here */}
      <div className="auth-card" data-testid="auth-card">
        <div className="auth-tabs" role="tablist" aria-label={_tr("Sign in or create account")}>
          <Link
            to={`/login${qs}`}
            role="tab"
            aria-selected={isLogin}
            className={"auth-tab" + (isLogin ? " is-active" : "")}
            data-testid="auth-tab-login"
          >
            {_tr("Sign in")}
          </Link>
          <Link
            to={`/signup${qs}`}
            role="tab"
            aria-selected={!isLogin}
            className={"auth-tab" + (!isLogin ? " is-active" : "")}
            data-testid="auth-tab-signup"
          >
            {_tr("Create account")}
          </Link>
        </div>
        {children}
      </div>

      {/* Below-card hero block: big editorial headline + three trust signals */}
      <div className="auth-below">
        <h2 className="auth-headline">
          {_tr("Your notes,")} <em>{_tr("exam-ready.")}</em>
        </h2>
        <div className="auth-trust">
          <span className="auth-trust-item">
            <svg viewBox="0 0 14 14" fill="none" aria-hidden="true"><path d="M3 7.5l2.5 2.5L11 4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/></svg>
            {_tr("No card required")}
          </span>
          <span className="auth-trust-item">
            <svg viewBox="0 0 14 14" fill="none" aria-hidden="true"><path d="M7 1.5l1.7 3.5 3.8.55-2.75 2.7.65 3.8L7 10.15 3.6 11.95l.65-3.8L1.5 5.45l3.8-.55L7 1.5z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round"/></svg>
            {_tr("Built for students")}
          </span>
          <span className="auth-trust-item">
            <svg viewBox="0 0 14 14" fill="none" aria-hidden="true"><path d="M2 7a5 5 0 0110 0v.5a5 5 0 01-10 0V7zm5-3v3l2 1" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/></svg>
            {_tr("Set up in 30 seconds")}
          </span>
        </div>
      </div>
    </div>
  );
}

function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  // Bottom-of-form link to signup — carry the invite token across.
  const qs = typeof window !== "undefined" ? window.location.search : "";

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    const url = `${API}/auth/login`;
    const body = { email, password };
    try {
      const { data } = await axios.post(url, body, { timeout: 15000, withCredentials: true });
      saveSession(data);
      window.location.href = _notenestUrl();
      return;
    } catch (axiosErr) {
      // If axios couldn't even reach the server, try a clean fetch as a fallback —
      // strips interceptors, cookies, anything that might be in the way.
      const isNetwork = axiosErr && !axiosErr.response;
      if (isNetwork) {
        try {
          const r = await fetch(url, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
            credentials: "omit",
          });
          if (r.ok) {
            const data = await r.json();
            saveSession(data);
            window.location.href = _notenestUrl();
            return;
          }
          let detail = `Request failed (${r.status}).`;
          try {
            const j = await r.json();
            if (j && j.detail) detail = formatApiError(j.detail);
          } catch (_) {}
          setErr(detail);
          return;
        } catch (_) {
          /* fall through to network message below */
        }
      }
      setErr(describeAxiosError(axiosErr));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Shell mode="login">
      <h1 className="auth-title" data-testid="login-title">{_tr("Welcome back")}</h1>
      <p className="auth-sub">{_tr("Sign in and pick up where you left off.")}</p>
      <form onSubmit={submit} className="auth-form" data-testid="login-form">
        <label className="auth-label auth-form-full">
          {_tr("Email")}
          <input
            type="email"
            required
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="auth-input"
            data-testid="login-email-input"
            placeholder={_tr("you@school.edu")}
          />
        </label>
        <label className="auth-label auth-form-full">
          {_tr("Password")}
          <input
            type="password"
            required
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="auth-input"
            data-testid="login-password-input"
            placeholder="••••••••"
          />
        </label>
        <div className="auth-forgot auth-form-full">
          <a href="/forgot-password.html" className="auth-link" data-testid="forgot-password-link">
            {_tr("Forgot password?")}
          </a>
        </div>
        {err && <div className="auth-error auth-form-full" data-testid="login-error">{err}</div>}
        <button type="submit" className="auth-btn auth-form-full" disabled={loading} data-testid="login-submit-button">
          {loading ? (<><span className="auth-btn-spinner" aria-hidden="true" />{_tr("Signing in…")}</>) : _tr("Sign in")}
        </button>
      </form>
      <div className="auth-switch">
        {_tr("New to NoteNest?")}{" "}
        <Link to={`/signup${qs}`} className="auth-link" data-testid="go-signup-link">{_tr("Create an account")}</Link>
      </div>
    </Shell>
  );
}

function Signup() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  // Bottom-of-form link to login — carry the invite token across.
  const qs = typeof window !== "undefined" ? window.location.search : "";

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    const url = `${API}/auth/register`;
    const browserLang = (navigator.language || "en").slice(0, 2).toLowerCase();
    const body = { email, password, name, language: browserLang };
    try {
      const { data } = await axios.post(url, body, { timeout: 15000, withCredentials: true });
      saveSession(data);
      window.location.href = _notenestUrl();
      return;
    } catch (axiosErr) {
      const isNetwork = axiosErr && !axiosErr.response;
      if (isNetwork) {
        try {
          const r = await fetch(url, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
            credentials: "omit",
          });
          if (r.ok) {
            const data = await r.json();
            saveSession(data);
            window.location.href = _notenestUrl();
            return;
          }
          let detail = `Request failed (${r.status}).`;
          try {
            const j = await r.json();
            if (j && j.detail) detail = formatApiError(j.detail);
          } catch (_) {}
          setErr(detail);
          return;
        } catch (_) {}
      }
      setErr(describeAxiosError(axiosErr));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Shell mode="signup">
      <h1 className="auth-title" data-testid="signup-title">{_tr("Create your account")}</h1>
      <p className="auth-sub">{_tr("Free forever for your first notes. No card required.")}</p>
      <form onSubmit={submit} className="auth-form" data-testid="signup-form">
        <label className="auth-label">
          {_tr("Name")}
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="auth-input"
            data-testid="signup-name-input"
            placeholder={_tr("Alex Taylor")}
          />
        </label>
        <label className="auth-label">
          {_tr("Email")}
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="auth-input"
            data-testid="signup-email-input"
            placeholder={_tr("you@school.edu")}
          />
        </label>
        <label className="auth-label auth-form-full">
          {_tr("Password")}
          <input
            type="password"
            required
            minLength={6}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="auth-input"
            data-testid="signup-password-input"
            placeholder={_tr("At least 6 characters")}
          />
        </label>
        {err && <div className="auth-error auth-form-full" data-testid="signup-error">{err}</div>}
        <button type="submit" className="auth-btn auth-form-full" disabled={loading} data-testid="signup-submit-button">
          {loading ? (<><span className="auth-btn-spinner" aria-hidden="true" />{_tr("Creating…")}</>) : _tr("Create account")}
        </button>
      </form>
      <div className="auth-switch">
        {_tr("Already have an account?")}{" "}
        <Link to={`/login${qs}`} className="auth-link" data-testid="go-login-link">{_tr("Sign in")}</Link>
      </div>
    </Shell>
  );
}

function Gate() {
  const [state, setState] = useState("loading");
  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      setState("guest");
      return;
    }
    let canceled = false;
    axios
      .get(`${API}/auth/me`)
      .then(() => { if (!canceled) setState("auth"); })
      .catch(() => {
        localStorage.removeItem(TOKEN_KEY);
        if (!canceled) setState("guest");
      });
    return () => { canceled = true; };
  }, []);
  if (state === "loading") {
    return (
      <div className="auth-wrap" data-testid="root-loading">
        <div className="auth-spinner" />
      </div>
    );
  }
  if (state === "auth") {
    window.location.href = _notenestUrl();
    return null;
  }
  // Guest path — but preserve the invite token across the login redirect
  // so it survives login/signup → /notenest.html.
  const qs = _passthroughQuery();
  if (qs) return <Navigate to={`/login${qs}`} replace />;
  return <Navigate to="/login" replace />;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Gate />} />
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
